import React from 'react';
import { COLORS } from './constants';
import { ReportIcon } from '../Earnings';
import type { EarningsStatsSummary } from './prepareEarnings';

interface Props {
  summary: EarningsStatsSummary;
  /** Quarters in the current window, for the beat-rate caption. */
  windowLabel: string;
}

function pct(n: number): string {
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
}

function Tile({ label, children, caption }: {
  label: string;
  children: React.ReactNode;
  caption?: string;
}): React.ReactElement {
  return (
    <div className="rounded border border-dark-border bg-dark-surface px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="mt-0.5 font-numeric text-lg leading-tight text-white">{children}</div>
      {caption && <div className="text-[10px] text-gray-500">{caption}</div>}
    </div>
  );
}

/** Signed value in the beat/miss colours, or an em dash when unmeasurable. */
function Surprise({ value }: { value: number | null }): React.ReactElement {
  if (value == null) return <span className="text-gray-500">—</span>;
  return <span style={{ color: value >= 0 ? COLORS.beat : COLORS.miss }}>{pct(value)}</span>;
}

/**
 * The headline read on the window the charts are showing.
 *
 * Sits above the charts because it answers the question most readers arrive
 * with — "does this company beat?" — in one line, before they parse any bars.
 */
export function EarningsStats({ summary, windowLabel }: Props): React.ReactElement {
  const { beats, measured, avgEpsSurprise, avgRevenueSurprise, next } = summary;
  const rate = measured ? Math.round((beats / measured) * 100) : null;

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Tile label="EPS Beat Rate" caption={measured ? `${beats} of ${measured} · ${windowLabel}` : undefined}>
        {rate == null ? (
          <span className="text-gray-500">—</span>
        ) : (
          <span style={{ color: rate >= 50 ? COLORS.beat : COLORS.miss }}>{rate}%</span>
        )}
      </Tile>

      <Tile label="Avg EPS Surprise" caption={windowLabel}>
        <Surprise value={avgEpsSurprise} />
      </Tile>

      <Tile label="Avg Rev Surprise" caption={windowLabel}>
        <Surprise value={avgRevenueSurprise} />
      </Tile>

      <Tile label="Next Report" caption={next ? next.label : undefined}>
        {next ? (
          <span className="flex items-center gap-1.5 text-accent-yellow">
            {next.date}
            <ReportIcon time={next.time} className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span className="text-gray-500">—</span>
        )}
      </Tile>
    </div>
  );
}
